/**
 * InterFresh — Wishlist System
 * Persistent localStorage wishlist with interactive drawer, toasts, and real-time badge updates.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'interfresh_wishlist';

  var Wishlist = {
    get: function () {
      try {
        var data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : [];
      } catch (e) {
        console.error('Wishlist read error:', e);
        return [];
      }
    },

    set: function (items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        console.error('Wishlist save error:', e);
      }
      this.updateBadges();
      this.updateButtons();
      this.renderDrawer();
      this.renderPageGrid();
    },

    has: function (id) {
      if (!id) return false;
      var items = this.get();
      return items.some(function (item) {
        return String(item.id) === String(id);
      });
    },

    add: function (product) {
      if (!product || !product.id) return;
      var items = this.get();
      if (!this.has(product.id)) {
        items.unshift(product);
        this.set(items);
        this.showToast('Saved to Wishlist', 'added');
      }
    },

    remove: function (id) {
      if (!id) return;
      var items = this.get();
      var filtered = items.filter(function (item) {
        return String(item.id) !== String(id);
      });
      this.set(filtered);
      this.showToast('Removed from Wishlist', 'removed');
    },

    toggle: function (product) {
      if (!product || !product.id) return;
      if (this.has(product.id)) {
        this.remove(product.id);
      } else {
        this.add(product);
      }
    },

    updateBadges: function () {
      var items = this.get();
      var count = items.length;

      document.querySelectorAll('.wishlist-badge, #WishlistBadge, #WishlistDrawerCount, .wishlist-count-text').forEach(function (badge) {
        badge.textContent = count;
        if (badge.classList.contains('wishlist-badge') || badge.id === 'WishlistBadge') {
          if (count > 0) {
            badge.style.display = 'flex';
          } else {
            badge.style.display = 'none';
          }
        }
      });
    },

    updateButtons: function () {
      var self = this;
      document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
        var id = btn.getAttribute('data-product-id');
        var isWishlisted = self.has(id);
        if (isWishlisted) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-label', 'Remove from wishlist');
          btn.setAttribute('title', 'Remove from Wishlist');
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-label', 'Save to wishlist');
          btn.setAttribute('title', 'Save to Wishlist');
        }
      });
    },

    showToast: function (message, type) {
      var toast = document.getElementById('WishlistToast');
      var msgEl = document.getElementById('WishlistToastMsg');
      if (!toast || !msgEl) return;

      msgEl.textContent = message;
      toast.classList.remove('is-active', 'type-added', 'type-removed');
      toast.classList.add('is-active');
      if (type) toast.classList.add('type-' + type);

      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(function () {
        toast.classList.remove('is-active');
      }, 2600);
    },

    openDrawer: function () {
      var drawer = document.getElementById('WishlistDrawer');
      var backdrop = document.getElementById('WishlistBackdrop');
      if (!drawer || !backdrop) return;

      this.renderDrawer();
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      document.body.classList.add('wishlist-drawer-active');
    },

    closeDrawer: function () {
      var drawer = document.getElementById('WishlistDrawer');
      var backdrop = document.getElementById('WishlistBackdrop');
      if (drawer) drawer.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('wishlist-drawer-active');
    },

    renderDrawer: function () {
      var container = document.getElementById('WishlistDrawerBody');
      if (!container) return;

      var items = this.get();
      if (items.length === 0) {
        container.innerHTML = [
          '<div class="wishlist-empty-state">',
          '  <div class="wishlist-empty-icon">',
          '    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">',
          '      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>',
          '    </svg>',
          '  </div>',
          '  <h3>Your Wishlist is Empty</h3>',
          '  <p>Save your favorite air-purifying desk plants by tapping the heart icon on any product.</p>',
          '  <a href="/collections/all" class="btn-primary" style="margin-top: 16px; padding: 12px 24px; font-size: 0.9rem;">Explore Desk Plants</a>',
          '</div>'
        ].join('\n');
        return;
      }

      var html = '<div class="wishlist-items-list">';
      items.forEach(function (item) {
        var imgHtml = item.image
          ? '<img src="' + item.image + '" alt="' + (item.title || '') + '" width="70" height="70" loading="lazy">'
          : '<div class="wishlist-no-img">🪴</div>';

        var comparePriceHtml = item.compare_price
          ? '<span class="wishlist-item-compare-price">' + item.compare_price + '</span>'
          : '';

        html += [
          '<div class="wishlist-item-card" data-item-id="' + item.id + '">',
          '  <a href="' + item.url + '" class="wishlist-item-image">',
          '    ' + imgHtml,
          '  </a>',
          '  <div class="wishlist-item-info">',
          '    <div class="wishlist-item-top">',
          '      <a href="' + item.url + '" class="wishlist-item-title">' + (item.title || '') + '</a>',
          '      <button type="button" class="wishlist-item-remove" data-remove-id="' + item.id + '" aria-label="Remove ' + (item.title || '') + '">',
          '        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
          '      </button>',
          '    </div>',
          '    <div class="wishlist-item-price-row">',
          '      <span class="wishlist-item-price">' + (item.price || '') + '</span>',
          '      ' + comparePriceHtml,
          '    </div>',
          '    <div class="wishlist-item-actions">',
          '      <button type="button" class="wishlist-atc-btn" data-add-variant="' + (item.variant_id || item.id) + '">',
          '        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>',
          '        Add to Cart',
          '      </button>',
          '    </div>',
          '  </div>',
          '</div>'
        ].join('\n');
      });
      html += '</div>';

      container.innerHTML = html;
    },

    renderPageGrid: function () {
      var grid = document.getElementById('WishlistPageGrid');
      if (!grid) return;

      var items = this.get();
      if (items.length === 0) {
        grid.innerHTML = [
          '<div class="wishlist-page-empty" style="grid-column: 1/-1; text-align: center; padding: 60px 20px;">',
          '  <div class="wishlist-empty-icon" style="margin: 0 auto 16px;">',
          '    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>',
          '  </div>',
          '  <h2 style="font-family: var(--font-display); font-size: 1.8rem; margin-bottom: 8px;">Your Wishlist is Empty</h2>',
          '  <p style="color: var(--color-text-muted); margin-bottom: 24px;">Explore our collection of soil-free desk plants and tap the heart to save your favorites.</p>',
          '  <a href="/collections/all" class="btn-primary" style="padding: 14px 28px;">Explore All Plants</a>',
          '</div>'
        ].join('\n');
        return;
      }

      var html = '';
      items.forEach(function (item) {
        var imgHtml = item.image
          ? '<img src="' + item.image + '" alt="' + (item.title || '') + '" loading="lazy">'
          : '<div class="placeholder-svg">🪴</div>';

        html += [
          '<div class="product-card new-card-style wishlist-page-card" data-item-id="' + item.id + '">',
          '  <button type="button" class="wishlist-toggle-btn is-active card-wishlist-btn" data-wishlist-btn data-product-id="' + item.id + '" aria-label="Remove from wishlist">',
          '    <svg class="wishlist-heart-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>',
          '  </button>',
          '  <a href="' + item.url + '" class="product-image-wrap">',
          '    ' + imgHtml,
          '  </a>',
          '  <div class="product-info">',
          '    <h3 class="product-title"><a href="' + item.url + '">' + (item.title || '') + '</a></h3>',
          '    <div class="product-bottom-row">',
          '      <div class="price-block">',
          '        <span class="price-current">' + (item.price || '') + '</span>',
          '      </div>',
          '    </div>',
          '    <div class="card-btn-group">',
          '      <button type="button" class="btn-card-atc" data-variant-id="' + (item.variant_id || item.id) + '" style="flex: 1;">',
          '        <span>Add to Cart</span>',
          '      </button>',
          '    </div>',
          '  </div>',
          '</div>'
        ].join('\n');
      });

      grid.innerHTML = html;
    },

    quickAddToCart: function (variantId, btnElement) {
      if (!variantId) return;
      var self = this;
      var originalText = btnElement ? btnElement.innerHTML : '';
      if (btnElement) {
        btnElement.disabled = true;
        btnElement.innerHTML = 'Adding...';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (btnElement) {
            btnElement.innerHTML = '✓ Added!';
            setTimeout(function () {
              btnElement.disabled = false;
              btnElement.innerHTML = originalText;
            }, 1800);
          }
          self.showToast('Added to Cart', 'added');
          // Update cart badge if present
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('.cart-badge').forEach(function (badge) {
                badge.textContent = cart.item_count;
                badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
              });
            });
        })
        .catch(function (err) {
          console.error('Cart add error:', err);
          if (btnElement) {
            btnElement.disabled = false;
            btnElement.innerHTML = originalText;
          }
          self.showToast('Could not add to cart', 'removed');
        });
    },

    init: function () {
      var self = this;

      // Initial state sync
      this.updateBadges();
      this.updateButtons();
      this.renderDrawer();
      this.renderPageGrid();

      // Delegated click handler for wishlist buttons
      document.addEventListener('click', function (e) {
        // Toggle wishlist button clicked
        var btn = e.target.closest('[data-wishlist-btn]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();

          var product = {
            id: btn.getAttribute('data-product-id'),
            variant_id: btn.getAttribute('data-variant-id') || btn.getAttribute('data-product-id'),
            handle: btn.getAttribute('data-product-handle'),
            title: btn.getAttribute('data-product-title'),
            price: btn.getAttribute('data-product-price'),
            price_raw: btn.getAttribute('data-product-price-raw'),
            compare_price: btn.getAttribute('data-product-compare-price'),
            image: btn.getAttribute('data-product-image'),
            url: btn.getAttribute('data-product-url'),
            available: btn.getAttribute('data-product-available') !== 'false'
          };

          self.toggle(product);
          return;
        }

        // Open Wishlist drawer trigger
        if (e.target.closest('#WishlistHeaderBtn, .open-wishlist-btn, [data-open-wishlist]')) {
          e.preventDefault();
          self.openDrawer();
          return;
        }

        // Close Wishlist drawer trigger
        if (e.target.closest('#WishlistDrawerClose, #WishlistBackdrop')) {
          e.preventDefault();
          self.closeDrawer();
          return;
        }

        // Remove item button inside drawer
        var removeBtn = e.target.closest('[data-remove-id]');
        if (removeBtn) {
          e.preventDefault();
          var removeId = removeBtn.getAttribute('data-remove-id');
          self.remove(removeId);
          return;
        }

        // Add to Cart button inside drawer
        var atcBtn = e.target.closest('.wishlist-atc-btn');
        if (atcBtn) {
          e.preventDefault();
          var variantId = atcBtn.getAttribute('data-add-variant');
          self.quickAddToCart(variantId, atcBtn);
          return;
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.closeDrawer();
        }
      });
    }
  };

  // Expose globally and initialize when DOM is ready
  window.InterFreshWishlist = Wishlist;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      Wishlist.init();
    });
  } else {
    Wishlist.init();
  }
})();
